"use client";

import { c as _c } from "react/compiler-runtime";
import { ArrowUpRight, Code2, Copy, Check, ExternalLink, Image as ImageIcon, MessageSquare, PanelTop, BarChart3, PenLine, ShieldCheck, Sparkles, Github, Play, Wallet, LayoutGrid, MessagesSquare, Presentation, Swords } from "lucide-react";
import Image from "next/image";
import { useState, useCallback } from "react";
import { Badge } from "@/lib/components/badge";
import { Button } from "@/lib/components/button";
import { Card } from "@/lib/components/card";
interface Template {
  name: string;
  description: string;
  href: string;
  demoUrl?: string;
  demoLabel?: string;
  image?: string;
  icon: typeof ImageIcon;
  tags: string[];
  gradient: string;
  featured?: boolean;
}
const templates: Template[] = [{
  name: "Embeddable Credits",
  description: 'Monetize your AI app in 5 minutes. Drop in a wallet + checkout so your end-users buy credits and use AI in-app, billed to their own balance — the "Stripe for AI" flagship.',
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/embeddable-credits",
  icon: Wallet,
  tags: ["TypeScript", "Next.js", "Embeddable SDK"],
  gradient: "from-emerald-500/20 via-teal-500/20 to-cyan-500/20",
  featured: true
}, {
  name: "Image Generation",
  description: "Generate stunning images with AI using multiple providers. Supports DALL-E, Stable Diffusion, and more through a unified API.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/image-generation",
  demoUrl: "https://llmgateway-templates-image-generation-124.meetploy.app",
  image: "/templates/image-gen.png",
  icon: ImageIcon,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-violet-500/20 via-fuchsia-500/20 to-pink-500/20",
  featured: true
}, {
  name: "AI Chatbot",
  description: "Streaming chat interface with conversation history and model selector. Switch between LLM providers on the fly with real-time token delivery.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/ai-chatbot",
  demoUrl: "https://llmgateway-templates-ai-chatbot-108.meetploy.app",
  image: "/templates/chatbot.png",
  icon: MessageSquare,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-sky-500/20 via-blue-500/20 to-indigo-500/20",
  featured: true
}, {
  name: "Model Arena",
  description: "Send one prompt to up to four models and compare the answers side by side, with live time to first token, tokens per second, and the exact cost of every response. Badges call out the fastest and cheapest model.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/model-arena",
  image: "/templates/model-arena.png",
  icon: Swords,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-lime-500/20 via-emerald-500/20 to-cyan-500/20",
  featured: true
}, {
  name: "AI Slides",
  description: "AI presentation builder. Generate a deck from a topic, research facts with web search, add AI images and data-backed charts, then export to PowerPoint.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/ai-slides",
  icon: Presentation,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-indigo-500/20 via-violet-500/20 to-purple-500/20"
}, {
  name: "OG Image Generator",
  description: "AI-powered Open Graph image generator with live preview, multiple themes, and one-click download. Uses structured output to generate title, subtitle, and call-to-action copy.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/og-image-generator",
  demoUrl: "https://llmgateway-templates-og-image-generator-926.meetploy.app",
  image: "/templates/og-image.png",
  icon: PanelTop,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-orange-500/20 via-amber-500/20 to-yellow-500/20"
}, {
  name: "Feedback Dashboard",
  description: "Customer feedback sentiment analysis dashboard. Paste reviews for batch AI analysis with sentiment scores, key themes extraction, and individual review breakdowns.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/feedback-dashboard",
  demoUrl: "https://llmgateway-templates-feedback-dashboard-189.meetploy.app",
  image: "/templates/feedback.png",
  icon: BarChart3,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-emerald-500/20 via-green-500/20 to-teal-500/20"
}, {
  name: "Writing Assistant",
  description: "AI writing assistant with text actions including rewrite, summarize, expand, fix grammar, and change tone. Supports multiple tone presets from professional to academic.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/writing-assistant",
  demoUrl: "https://llmgateway-templates-writing-assistant-229.meetploy.app",
  image: "/templates/writing-assistant.png",
  icon: PenLine,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-rose-500/20 via-pink-500/20 to-fuchsia-500/20"
}, {
  name: "QA Agent",
  description: "AI-powered QA testing agent that drives a real headless browser through Playwright. Describe tests in plain English and watch it navigate, click, and type step-by-step with a live preview and action timeline.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/qa-agent",
  demoUrl: "https://youtu.be/-ai9eVvXvZE",
  demoLabel: "Watch Demo",
  image: "/templates/qa-agent.png",
  icon: ShieldCheck,
  tags: ["TypeScript", "Next.js", "AI SDK 7"],
  gradient: "from-cyan-500/20 via-teal-500/20 to-blue-500/20"
}, {
  name: "Slack Q&A Bot",
  description: "Slack bot that streams AI answers into threads, keeps the conversation context, and searches the live web for time-sensitive questions. Built on Chat SDK and Hono.",
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/slack-qa-bot",
  icon: MessagesSquare,
  tags: ["TypeScript", "Chat SDK", "Hono"],
  gradient: "from-fuchsia-500/20 via-purple-500/20 to-indigo-500/20"
}, {
  name: "Showcase",
  description: 'A static, deployable gallery of apps built with LLM Gateway templates. Tag and type filtering, a "Submit your app" flow, and a Powered-By badge baked in — fork it or use the community directory.',
  href: "https://github.com/theopenco/llmgateway-templates/tree/main/templates/showcase",
  icon: LayoutGrid,
  tags: ["TypeScript", "Next.js", "Tailwind CSS"],
  gradient: "from-amber-500/20 via-orange-500/20 to-rose-500/20"
}];
export function TemplateCards() {
  const $ = _c(9);
  const [copiedUrl, setCopiedUrl] = useState(null);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = url => {
      const templateName = url.split("/").pop();
      navigator.clipboard.writeText(`npx @llmgateway/cli init --template ${templateName}`);
      setCopiedUrl(url);
      setTimeout(() => setCopiedUrl(null), 2000);
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const copyToClipboard = t0;
  if (templates.length === 0) {
    let t1;
    if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
      t1 = <div className="text-center py-16"><Sparkles className="mx-auto h-12 w-12 text-muted-foreground/50" /><p className="mt-4 text-muted-foreground">Templates coming soon. Check back later!</p></div>;
      $[1] = t1;
    } else {
      t1 = $[1];
    }
    return t1;
  }
  let t1;
  if ($[2] !== copiedUrl) {
    t1 = templates.map(template => <Card key={template.name} className="group relative flex flex-col overflow-hidden border-0 bg-gradient-to-br from-background to-muted/30 shadow-xl hover:shadow-2xl transition-all duration-500"><div className={`absolute inset-0 bg-gradient-to-br ${template.gradient} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />{template.featured && <div className="absolute top-4 right-4 z-10"><Badge className="bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white border-0 shadow-lg"><Sparkles className="mr-1 h-3 w-3" />Featured</Badge></div>}<div className="relative aspect-video overflow-hidden">{template.image ? <Image src={template.image} alt={`${template.name} preview`} width={1200} height={675} className="h-full w-full object-cover" /> : <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${template.gradient}`}><template.icon className="h-16 w-16 text-foreground/70" /></div>}<div className="absolute inset-0 bg-gradient-to-t from-background/80 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" /></div><div className="relative flex flex-1 flex-col p-6 space-y-6"><div className="space-y-3"><div className="flex items-center gap-3"><div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-violet-500 to-fuchsia-500 shadow-lg"><template.icon className="h-5 w-5 text-white" /></div><h3 className="text-2xl font-bold tracking-tight">{template.name}</h3></div><p className="text-muted-foreground leading-relaxed">{template.description}</p></div><div className="flex flex-wrap gap-2">{template.tags.map(_temp)}</div><div className="flex flex-col sm:flex-row gap-3 pt-2 mt-auto">{template.demoUrl && <Button asChild={true} className="flex-1 gap-2 font-semibold"><a href={template.demoUrl} target="_blank" rel="noopener noreferrer">{template.demoLabel ? <Play className="h-4 w-4" /> : <ExternalLink className="h-4 w-4" />}{template.demoLabel ?? "Live Demo"}<ArrowUpRight className="h-4 w-4 opacity-50 group-hover:opacity-100 transition-opacity" /></a></Button>}<Button variant={template.demoUrl ? "outline" : "default"} asChild={true} className={template.demoUrl ? "gap-2" : "flex-1 gap-2 font-semibold"}><a href={template.href} target="_blank" rel="noopener noreferrer"><Github className="h-4 w-4" />GitHub</a></Button><Button variant="outline" className="gap-2" onClick={() => copyToClipboard(template.href)}>{copiedUrl === template.href ? <><Check className="h-4 w-4 text-green-500" />Copied!</> : <><Copy className="h-4 w-4" />Clone</>}</Button></div></div><div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-violet-500 via-fuchsia-500 to-pink-500 opacity-0 group-hover:opacity-100 transition-opacity duration-500" /></Card>);
    $[2] = copiedUrl;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  let t2;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = <div className="flex h-16 w-16 items-center justify-center rounded-2xl bg-muted"><Sparkles className="h-8 w-8 text-muted-foreground" /></div>;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  let t3;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = <div className="space-y-2"><h3 className="text-xl font-semibold text-muted-foreground">More Coming Soon</h3><p className="text-sm text-muted-foreground/80">We're working on more templates. Have a suggestion?</p></div>;
    $[5] = t3;
  } else {
    t3 = $[5];
  }
  let t4;
  if ($[6] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <Card className="relative overflow-hidden border-dashed border-2 bg-muted/20 hover:bg-muted/30 transition-colors duration-300"><div className="p-6 space-y-6 flex flex-col items-center justify-center min-h-[320px] text-center">{t2}{t3}<Button variant="outline" asChild={true}><a href="https://github.com/theopenco/llmgateway-templates/issues/new" target="_blank" rel="noopener noreferrer">Request a Template</a></Button></div></Card>;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  let t5;
  if ($[7] !== t1) {
    t5 = <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-2 max-w-5xl mx-auto">{t1}{t4}</div>;
    $[7] = t1;
    $[8] = t5;
  } else {
    t5 = $[8];
  }
  return t5;
}
function _temp(tag) {
  return <Badge key={tag} variant="secondary" className="bg-muted/80 hover:bg-muted transition-colors"><Code2 className="mr-1 h-3 w-3" />{tag}</Badge>;
}